import { Command } from 'commander';
import { TeamManager } from '../core/team-manager';
import { NotificationManager } from '../core/notification-manager';
import { logger } from '../utils/logger';
import { withErrorHandling } from '../utils/error-handler';
import pc from 'picocolors';
import { Table } from 'console-table-printer';
import inquirer from 'inquirer';

export function createNotifyCommand(): Command {
  const command = new Command('notify');
  
  command
    .description('Notification management')
    .alias('n');

  // Send notification
  command
    .command('send')
    .description('Send a notification')
    .option('-t, --type <type>', 'Notification type (info/success/warning/error/task/team)', 'info')
    .option('-p, --priority <priority>', 'Priority (low/medium/high/urgent)', 'medium')
    .option('--to <memberId>', 'Send to specific member (default: broadcast)')
    .option('--title <title>', 'Notification title')
    .action(withErrorHandling(async (options) => {
      const teamManager = new TeamManager();
      const notificationManager = new NotificationManager(teamManager);
      await teamManager.initialize();
      await notificationManager.initialize();
      
      const currentUser = teamManager.getCurrentUser();
      if (!currentUser) {
        console.log(pc.red('No current user set. Use "workflow team me <memberId>" first.'));
        return;
      }
      
      // Interactive mode if no title provided
      let title = options.title;
      let message: string;
      
      if (!title) {
        const answers = await inquirer.prompt([
          {
            type: 'input',
            name: 'title',
            message: 'Notification title:',
            validate: (input) => input.length > 0
          },
          {
            type: 'input',
            name: 'message',
            message: 'Message:',
            validate: (input) => input.length > 0
          }
        ]);
        
        title = answers.title;
        message = answers.message;
      } else {
        const { message: msg } = await inquirer.prompt([{
          type: 'input',
          name: 'message',
          message: 'Message:',
          validate: (input) => input.length > 0
        }]);
        message = msg;
      }
      
      const notification = await notificationManager.send({
        type: options.type,
        title,
        message,
        from: currentUser.id,
        to: options.to,
        priority: options.priority,
        read: false
      });
      
      console.log(pc.green('✅ Notification sent!'));
      console.log(`ID: ${pc.cyan(notification.id)}`);
      console.log(`To: ${options.to ? teamManager.getMemberById(options.to)?.name : 'All team members'}`);
      
      await notificationManager.stop();
    }));

  // List notifications
  command
    .command('list')
    .description('List notifications')
    .alias('ls')
    .option('-u, --unread', 'Show only unread notifications')
    .option('-n, --number <number>', 'Number of notifications to show', '20')
    .action(withErrorHandling(async (options) => {
      const teamManager = new TeamManager();
      const notificationManager = new NotificationManager(teamManager);
      await teamManager.initialize();
      await notificationManager.initialize();
      
      const currentUser = teamManager.getCurrentUser();
      const notifications = await notificationManager.getUnreadNotifications(currentUser?.id);
      
      if (notifications.length === 0) {
        console.log(pc.yellow('No notifications found.'));
        await notificationManager.stop();
        return;
      }
      
      console.log(pc.bold('\n📬 Notifications:\n'));
      
      const table = new Table({
        columns: [
          { name: 'type', title: 'Type', alignment: 'left' },
          { name: 'title', title: 'Title', alignment: 'left' },
          { name: 'from', title: 'From', alignment: 'left' },
          { name: 'time', title: 'Time', alignment: 'left' },
          { name: 'priority', title: 'Priority', alignment: 'left' }
        ]
      });
      
      const displayNotifications = options.unread ? 
        notifications.filter(n => !n.read) : 
        notifications.slice(0, parseInt(options.number));
      
      displayNotifications.forEach(notification => {
        const typeIcon = notification.type === 'error' ? '❌' :
                        notification.type === 'warning' ? '⚠️' :
                        notification.type === 'success' ? '✅' :
                        notification.type === 'task' ? '📋' :
                        notification.type === 'team' ? '👥' : 'ℹ️';
        
        const fromMember = teamManager.getMemberById(notification.from);
        const time = new Date(notification.timestamp).toLocaleString();
        
        table.addRow({
          type: `${typeIcon} ${notification.type}`,
          title: notification.title,
          from: fromMember?.name || notification.from,
          time,
          priority: notification.priority
        });
      });
      
      table.printTable();
      
      const unreadCount = notifications.filter(n => !n.read).length;
      if (unreadCount > 0) {
        console.log(pc.dim(`\n${unreadCount} unread notifications`));
      }
      
      await notificationManager.stop();
    }));

  // Mark as read
  command
    .command('read')
    .description('Mark notifications as read')
    .argument('[notificationId]', 'Notification ID to mark as read')
    .option('--all', 'Mark all as read')
    .action(withErrorHandling(async (notificationId, options) => {
      const teamManager = new TeamManager();
      const notificationManager = new NotificationManager(teamManager);
      await teamManager.initialize();
      await notificationManager.initialize();
      
      if (options.all) {
        const currentUser = teamManager.getCurrentUser();
        const notifications = await notificationManager.getUnreadNotifications(currentUser?.id);
        
        for (const notification of notifications) {
          await notificationManager.markAsRead(notification.id);
        }
        
        console.log(pc.green(`✅ Marked ${notifications.length} notifications as read`));
      } else if (notificationId) {
        await notificationManager.markAsRead(notificationId);
        console.log(pc.green('✅ Notification marked as read'));
      } else {
        console.log(pc.red('Please provide a notification ID or use --all'));
      }
      
      await notificationManager.stop();
    }));

  // Start notification server
  command
    .command('server')
    .description('Start notification server (SSE)')
    .option('-p, --port <port>', 'Server port', '3456')
    .action(withErrorHandling(async (options) => {
      const teamManager = new TeamManager();
      const notificationManager = new NotificationManager(teamManager, {
        enableSSE: true,
        ssePort: parseInt(options.port)
      });
      
      await teamManager.initialize();
      await notificationManager.initialize();
      
      console.log(pc.green(`✅ Notification server started on port ${options.port}`));
      console.log(pc.dim(`SSE endpoint: http://localhost:${options.port}/events`));
      console.log(pc.dim('Press Ctrl+C to stop'));
      
      // Keep server running
      process.on('SIGINT', async () => {
        console.log('\nStopping notification server...');
        await notificationManager.stop();
        process.exit(0);
      });
      
      // Simulate some notifications for testing
      if (process.env.DEBUG) {
        setInterval(async () => {
          await notificationManager.send({
            type: 'info',
            title: 'Test Notification',
            message: `Server heartbeat at ${new Date().toLocaleTimeString()}`,
            from: 'system',
            priority: 'low',
            read: false
          });
        }, 30000);
      }
    }));

  // Configure notification channels
  command
    .command('config')
    .description('Configure notification channels')
    .action(withErrorHandling(async () => {
      const channels = await inquirer.prompt([
        {
          type: 'checkbox',
          name: 'enabledChannels',
          message: 'Select enabled notification channels:',
          choices: [
            { name: 'File Storage (always enabled)', value: 'file', checked: true, disabled: true },
            { name: 'Server-Sent Events (SSE)', value: 'sse', checked: true },
            { name: 'MCP Integration', value: 'mcp', checked: true },
            { name: 'Webhook', value: 'webhook', checked: false }
          ]
        }
      ]);
      
      // Configure webhook if selected
      let webhookConfig;
      if (channels.enabledChannels.includes('webhook')) {
        webhookConfig = await inquirer.prompt([
          {
            type: 'input',
            name: 'url',
            message: 'Webhook URL:',
            validate: (input) => {
              try {
                new URL(input);
                return true;
              } catch {
                return 'Please enter a valid URL';
              }
            }
          },
          {
            type: 'confirm',
            name: 'addHeaders',
            message: 'Add custom headers?',
            default: false
          }
        ]);
        
        if (webhookConfig.addHeaders) {
          const { headers } = await inquirer.prompt([{
            type: 'input',
            name: 'headers',
            message: 'Headers (JSON format):',
            validate: (input) => {
              try {
                JSON.parse(input);
                return true;
              } catch {
                return 'Please enter valid JSON';
              }
            }
          }]);
          webhookConfig.headers = JSON.parse(headers);
        }
      }
      
      // Save configuration
      console.log(pc.green('✅ Notification channels configured!'));
      console.log(pc.dim('Configuration saved to .workflow/notifications/config.json'));
    }));

  // Test notification
  command
    .command('test')
    .description('Send a test notification')
    .action(withErrorHandling(async () => {
      const teamManager = new TeamManager();
      const notificationManager = new NotificationManager(teamManager);
      await teamManager.initialize();
      await notificationManager.initialize();
      
      const currentUser = teamManager.getCurrentUser();
      if (!currentUser) {
        console.log(pc.red('No current user set. Use "workflow team me <memberId>" first.'));
        return;
      }
      
      const types = ['info', 'success', 'warning', 'error', 'task', 'team'];
      const randomType = types[Math.floor(Math.random() * types.length)];
      
      const notification = await notificationManager.send({
        type: randomType as any,
        title: 'Test Notification',
        message: `This is a test ${randomType} notification sent at ${new Date().toLocaleTimeString()}`,
        from: currentUser.id,
        priority: 'medium',
        read: false,
        actions: [
          { label: 'Acknowledge', action: 'ack', primary: true },
          { label: 'Dismiss', action: 'dismiss' }
        ]
      });
      
      console.log(pc.green('✅ Test notification sent!'));
      console.log(`Type: ${notification.type}`);
      console.log(`ID: ${notification.id}`);
      
      await notificationManager.stop();
    }));

  return command;
}